import { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { ErpCopilotChat } from './erpHost';
import { DropdownMenu } from '@/components/ui/DropdownMenu';
import { IconHelp } from '@/components/ui/Icon';
import { useCopilotLayout } from '@/shared/store/copilotLayoutStore';
import { useHelpStore } from '@/shared/store/helpStore';
import { ASSISTANT_NAME } from '@/shared/branding';
import { useBottomLayout } from '@/shared/store/bottomChromeStore';

// Quick-create menu (relocated from the top nav). Lives in the copilot header.
const CREATE_ITEMS = [
  { label: 'Invoice', to: '/ar-invoices/new' },
  { label: 'Bill', to: '/ap-invoices/new' },
  { label: 'Sales Order', to: '/sales-orders/new' },
  { label: 'Purchase Order', to: '/purchase-orders/new' },
  { label: 'Receipt', to: '/receipts/new', divider: true },
  { label: 'Payment', to: '/payments/new' },
  { label: 'Customer', to: '/customers/new', divider: true },
  { label: 'Vendor', to: '/vendors/new' },
  { label: 'Item', to: '/items/new' },
  { label: 'BOM', to: '/boms/new' },
  { label: 'Inventory Tx', to: '/inventory/new' },
];

// Docked-panel geometry. The reserve MUST clear the panel's full footprint
// (width + right inset): full-bleed pages (Home, /bi) pad only 10px, so any
// shortfall puts the panel on top of content — the page's own padding is
// what provides the visual gap, not the reserve.
export const DOCK_PANEL_W_PX = 350;    // the docked card's own width
export const DOCK_RIGHT_INSET_PX = 10; // gap between the card and the screen edge
export const DOCK_RESERVE_PX = DOCK_PANEL_W_PX + DOCK_RIGHT_INSET_PX;
const DOCK_PANEL_W = `${DOCK_PANEL_W_PX}px`;
const DOCK_RESERVE = `${DOCK_RESERVE_PX}px`;

// Apply/remove the dock's page-layout side effects:
//  - --copilot-dock margin reserve on <main> (the panel's full footprint);
//  - data-copilot-docked on <html>, which collapses <main>'s right padding
//    to the system 4px container gap (index.css) so content sits exactly
//    one system gap away from the panel on EVERY page, whatever the page's
//    normal padding is.
export function applyDockLayout(docked: boolean) {
  document.documentElement.style.setProperty('--copilot-dock', docked ? DOCK_RESERVE : '0px');
  document.documentElement.toggleAttribute('data-copilot-docked', docked);
}

// The floating panel (open, not docked) covers the bottom-right corner where
// the toast sits; index.css keys on this attribute to move the toast clear.
export function applyFloatLayout(floatOpen: boolean) {
  document.documentElement.toggleAttribute('data-copilot-float', floatOpen);
}

// The launcher's real footprint, published for the bottom bars: the distance
// from the viewport's right edge to the launcher's left edge, plus an 8px gap.
// Its width depends on font metrics and the Appearance size, so no fixed
// reserve fits every machine. 0 when the launcher is docked, open or hidden.
export const COMPANION_SPACE_VAR = '--companion-launcher-space';
const COMPANION_GAP_PX = 8;
function setLauncherSpace(px: number) {
  document.documentElement.style.setProperty(COMPANION_SPACE_VAR, `${px}px`);
}

// Turn a route path into a human label for AI context, e.g.
// "/financial-overview" -> "Financial Overview", "/" -> "Dashboard".
function pageLabel(path: string): string {
  const seg = path.split('/').filter(Boolean)[0];
  if (!seg) return 'Dashboard';
  return seg.replace(/-/g, ' ').replace(/\b\w/g, (c) => c.toUpperCase());
}

/**
 * Global AI copilot. The display style is a per-user preference
 * (Settings → Appearance), with a quick toggle in the header:
 *   docked   — a persistent right-hand panel; the page reserves space via the
 *              --copilot-dock CSS var (read by AppLayout + the bottom bars).
 *   floating — a launcher pinned bottom-right that opens a slide-over (⌘/Ctrl-K).
 */
export function CopilotWidget() {
  // Persistent like the docked mode: stays open across page/menu changes and
  // reloads until the user closes it (localStorage keeps the flag per browser).
  const [open, setOpenState] = useState<boolean>(() => {
    try { return localStorage.getItem('mrpcomp:copilot-open') === '1'; } catch { return false; }
  });
  const setOpen = (v: boolean | ((o: boolean) => boolean)) => setOpenState((o) => {
    const next = typeof v === 'function' ? v(o) : v;
    try { localStorage.setItem('mrpcomp:copilot-open', next ? '1' : '0'); } catch { /* private mode */ }
    return next;
  });
  const layout = useCopilotLayout((s) => s.layout);
  const { fab } = useBottomLayout();
  const setLayout = useCopilotLayout((s) => s.setLayout);
  const docked = layout === 'docked';
  const [launcher, setLauncher] = useState<HTMLButtonElement | null>(null);
  const loc = useLocation();
  const nav = useNavigate();
  const openHelp = useHelpStore((s) => s.openHelp);
  const HelpBtn = (
    <button onClick={() => openHelp('ai-assistant')} title={`How to use the ${ASSISTANT_NAME}`}
      aria-label={`${ASSISTANT_NAME} help`}
      className="shrink-0 text-secondary hover:text-primary transition p-1"><IconHelp size={15} /></button>
  );
  const createItems = CREATE_ITEMS.map((it) => ({
    label: it.label, divider: it.divider,
    onClick: () => { setOpen(false); nav(it.to); },
  }));

  // Drive the layout-reservation var + padding-collapse attribute from the
  // docked state.
  useEffect(() => {
    applyDockLayout(docked);
    return () => applyDockLayout(false);
  }, [docked]);

  // Publish the floating launcher's footprint (pill or phone FAB); 0 when it
  // is not on screen.
  useEffect(() => {
    if (!launcher) { setLauncherSpace(0); return; }
    const measure = () => setLauncherSpace(
      Math.max(0, Math.round(document.documentElement.clientWidth - launcher.getBoundingClientRect().left + COMPANION_GAP_PX)));
    measure();
    const ro = typeof ResizeObserver === 'undefined' ? null : new ResizeObserver(measure);
    ro?.observe(launcher);
    window.addEventListener('resize', measure);
    return () => { ro?.disconnect(); window.removeEventListener('resize', measure); setLauncherSpace(0); };
  }, [launcher]);

  useEffect(() => {
    applyFloatLayout(open && !docked);
    return () => applyFloatLayout(false);
  }, [open, docked]);

  // ⌘/Ctrl-K toggles the floating drawer (only relevant when not docked).
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if ((e.metaKey || e.ctrlKey) && (e.key === 'k' || e.key === 'K')) {
        if (docked) return;
        e.preventDefault();
        setOpen((o) => !o);
      } else if (e.key === 'Escape') {
        if (e.defaultPrevented) return; // an overlay above the Companion already took this Escape
        setOpen(false);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [docked]);


  const NewMenu = (
    <DropdownMenu align="left" items={createItems} trigger={
      <span className="inline-flex items-center gap-1 text-[0.75rem] font-medium text-primary border border-primary/40 rounded-lg px-2 h-7 hover:bg-primary/5 transition cursor-pointer" title="Create a new record">
        <span className="text-[0.875rem] leading-none">＋</span> New
      </span>
    } />
  );
  // No logo on the Companion, in any form (owner, 2026-11-03): the Fritech logo
  // sits at the left of the bottom bar (BottomBar).
  const Title = (
    <div className="text-[0.875rem] font-semibold text-primary-text">{ASSISTANT_NAME}</div>
  );

  // ---- Docked panel ----
  // Sits BELOW the top nav (~80px = the floating MainNav bar 40 + the
  // floating SubNav bar 40) so the top chrome stays continuous and the
  // panel reads as an integrated right sidebar. `bottom` clears the bottom
  // nav bar.
  if (docked) {
    return (
      // An inset rounded "card" floating on the canvas (gaps top/right/bottom)
      // so it matches the app's other panels instead of being a flush slab.
      <aside data-copilot="" className="fixed z-30 flex flex-col bg-surface border border-border rounded-2xl shadow-sm overflow-hidden"
        style={{ width: DOCK_PANEL_W, top: 'var(--app-nav-h, 80px)', right: `${DOCK_RIGHT_INSET_PX}px`, bottom: '48px' }}>
        <div className="flex items-center justify-between px-3 h-9 border-b border-border-light shrink-0">
          <div className="flex items-center gap-2">{Title}{NewMenu}</div>
          <div className="flex items-center gap-0.5 -mr-1">
            {HelpBtn}
            <button onClick={() => setLayout('floating')} title="Undock — float the assistant"
              className="shrink-0 text-secondary hover:text-primary-text transition p-1"><CollapseIcon /></button>
          </div>
        </div>
        <div className="flex-1 min-h-0 px-3 pt-2 pb-3">
          <ErpCopilotChat context={pageLabel(loc.pathname)} />
        </div>
      </aside>
    );
  }

  // ---- Floating launcher + slide-over ----
  return (
    <>
      {!open && (fab ? (
        // Phone page with no bottom band (D6): a 44px round button in the corner.
        <button ref={setLauncher} onClick={() => setOpen(true)} title={`Ask the ${ASSISTANT_NAME} (⌘K)`} aria-label={`Ask the ${ASSISTANT_NAME}`}
          className="fixed bottom-3 right-3 z-40 inline-flex items-center justify-center h-11 w-11 rounded-full bg-primary text-white text-[0.875rem] font-semibold shadow-md hover:brightness-110 transition">
          <span aria-hidden="true">AI</span>
        </button>
      ) : (
        <button ref={setLauncher} onClick={() => setOpen(true)} title={`Ask the ${ASSISTANT_NAME} (⌘K)`}
          // Sits ON the persistent bottom bar (bar: bottom-2 h-[34px] → spans
          // 8..42px): h-6 centered vertically at bottom-[13px], inset from the
          // bar's right edge. z-40 keeps it above the bar (z-20) + pagination.
          className="fixed bottom-[13px] right-[18px] z-40 flex items-center rounded-full bg-primary text-white shadow-sm hover:brightness-110 px-3 h-6 transition">
          <span className="text-[0.75rem] font-semibold">{ASSISTANT_NAME}</span>
        </button>
      ))}

      {open && (
        <div data-copilot="" className="fixed inset-0 z-50 pointer-events-none" role="dialog">
          {/* Persistent floating window: the page behind stays clickable and
              scrollable (pointer-events pass through the wrapper); navigating
              between forms/menus keeps it open — only Close/Esc/Dock close it. */}
          {/* Floating card (half-height) hovering over the page: curved edges,
              deep soft shadow, and a float-in entrance (index.css .copilot-float).
              Anchored above the bottom bar so it never covers the launcher's own row. */}
          <aside onClick={(e) => e.stopPropagation()}
            className={`copilot-float pointer-events-auto absolute right-3 ${fab ? 'bottom-[64px]' : 'bottom-[52px]'} h-[50vh] min-h-[320px] w-[calc(100%-1.5rem)] sm:w-[500px] bg-surface rounded-2xl border border-border overflow-hidden flex flex-col shadow-[0_28px_70px_-16px_rgba(15,23,42,0.45),0_8px_20px_-8px_rgba(15,23,42,0.25)] ring-1 ring-black/5`}>
            <div className="flex items-center justify-between px-4 h-12 border-b border-border shrink-0">
              <div className="flex items-center gap-2.5">{Title}{NewMenu}</div>
              <div className="flex items-center gap-3">
                {HelpBtn}
                <button onClick={() => { setLayout('docked'); setOpen(false); }} title="Dock to the side"
                  className="text-[0.6875rem] font-medium text-secondary hover:text-primary-text transition">Dock ⇥</button>
                <button aria-label="Close" onClick={() => setOpen(false)}
                  className="text-muted hover:text-primary-text text-2xl leading-none">×</button>
              </div>
            </div>
            <div className="flex-1 min-h-0 p-4">
              <ErpCopilotChat context={pageLabel(loc.pathname)} />
            </div>
          </aside>
        </div>
      )}
    </>
  );
}

function CollapseIcon() {
  return (
    <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
      <rect x="3" y="4" width="18" height="16" rx="2" />
      <path d="M14 4v16" />
      <path d="m7 9 3 3-3 3" />
    </svg>
  );
}
